import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { OrgDBScopedMergeRealTimeWritebackConfig } from 'libs/end-to-end/src/types/databases/merge-realtime-writebacks/merge-real-time-writeback-config';
import { OrgDBScopedWriteableMergeRealTimeWritebackConfig } from 'libs/end-to-end/src/types/databases/merge-realtime-writebacks/merge-real-time-writeback-config';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { UnknownObject } from 'libs/end-to-end/src/generic';

export const MergeRealtimeWritebackConfigsPage = () => {
  const { organizationId, databaseId } = useParams();

  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId) {
    throw new Error(
      'MergeRealtimeWritebackConfigsPage requires organizationId and databaseId in the URL parameters.',
    );
  }

  const getUrlFn = useCallback(
    () =>
      `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs`,
    [databaseId, organizationId],
  );
  const readAllPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: OrgDBScopedMergeRealTimeWritebackConfig.array(),
  });

  const isReadLoading = useMemo(() => {
    return readAllPattern.status === 'loading';
  }, [readAllPattern]);

  const createPattern = useSimpleWritePattern({
    getUrlFn,
    canWrite: !isReadLoading,
    writeSchema: OrgDBScopedWriteableMergeRealTimeWritebackConfig,
    responseSchema: UnknownObject,
  });

  const items: OrgDBScopedMergeRealTimeWritebackConfig[] = useMemo(() => {
    if (readAllPattern.status === 'done') {
      return readAllPattern.entity;
    }
    return [];
  }, [readAllPattern]);

  const emptyStateUi = useMemo(() => {
    if (readAllPattern.status === 'loading') {
      return null;
    }
    if (readAllPattern.status === 'error') {
      return JSON.stringify(readAllPattern.error);
    }
    return 'No merge realtime writeback configs found.';
  }, [readAllPattern]);

  return (
    <AdminLayout currentPage="MergeRealtimeConfigsPage">
      <Spacing direction="vertical" size="md">
        <AdminBreadcrumbs
          items={[
            { label: 'Home', to: '/admin' },
            { label: 'Organizations', to: '/organizations' },
            {
              label: 'Databases',
              to: `/organizations/${organizationId}/databases`,
            },
            {
              label: 'Edit Database',
              to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
            },
            {
              label: 'Merge Realtime Writeback Configs',
              to: `/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs`,
            },
          ]}
        />

        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Merge Realtime Writeback Configs'}
        />

        <Button
          variant="primary"
          size="small"
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (createPattern.canWrite) {
                createPattern.saveEntity(
                  {
                    name: 'New Config',
                    dataSetId: '',
                    linkedAccountId: 'pending',
                    aiInterviewerConfig: null,
                    aiInterviewerWritebackConfig: null,
                    resumeParserEnabled: true,
                    resumeGradingConfig: null,
                    resumeGradingWritebackConfig: null,
                    hiringReadinessWritebackConfig: null,
                  },
                  () => {
                    if (readAllPattern.status !== 'loading') {
                      readAllPattern.reloadEntity();
                    }
                  },
                );
              }
            },
          }}
        >
          Create New Config
        </Button>

        <Table
          items={items}
          isLoading={isReadLoading}
          empty={emptyStateUi}
          columnDefinitions={[
            {
              id: 'name',
              header: () => 'Name',
              cell: ({ item }) => (
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={item.name}
                />
              ),
            },
            {
              id: 'Edit',
              header: () => 'Edit',
              cell: ({ item }) => (
                <Button
                  variant="outline"
                  size="small"
                  behavior={{
                    type: 'onClick',
                    onClick: () => {
                      navigate(
                        `/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs/${item.id}`,
                      );
                    },
                  }}
                >
                  Edit
                </Button>
              ),
            },
          ]}
        />
      </Spacing>
    </AdminLayout>
  );
};
